import React, { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { createWalletCard, downloadWalletCard } from "../services/walletCards";

function readLocalUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getDeviceType() {
  if (typeof navigator === "undefined") return "ANDROID";
  const ua = navigator.userAgent || "";
  if (/iphone|ipad|ipod/i.test(ua)) return "IOS";
  if (/android/i.test(ua)) return "ANDROID";
  return "ANDROID";
}

export default function Settings() {
  const user = useMemo(() => readLocalUser(), []);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState(null);
  const [walletInfo, setWalletInfo] = useState(null);

  const role = useMemo(() => {
    const raw = user?.role ?? user?.typeUser ?? user?.type ?? user?.rol ?? null;
    return raw ? String(raw).toUpperCase() : "—";
  }, [user]);

  const companyName =
    user?.company?.name ??
    user?.companyName ??
    user?.company_name ??
    "—";

  const fields = [
    { label: "Nombre", value: user?.name ?? "—" },
    { label: "Identificación", value: user?.identification ?? user?.dni ?? "—" },
    { label: "Email", value: user?.email ?? user?.mail ?? "—" },
    { label: "Teléfono", value: user?.phone ?? "—" },
    { label: "Rol", value: role },
    { label: "Empresa", value: companyName },
  ];

  const userId = user?.id ?? user?.userId ?? user?.user_id ?? null;

  const handleDownloadWallet = async () => {
    if (!userId) {
      setWalletError("No se pudo resolver el ID del usuario.");
      return;
    }
    setWalletLoading(true);
    setWalletError(null);
    try {
      const deviceType = getDeviceType();
      await createWalletCard({ userId, deviceType });
      const downloaded = await downloadWalletCard({ userId, deviceType });
      setWalletInfo(downloaded);
    } catch (e) {
      setWalletError(String(e));
    } finally {
      setWalletLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Configuración</h1>
        <p className="text-gray-600">
          Datos del usuario y acceso a la wallet.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded shadow p-4">
          <h2 className="font-semibold mb-3">Datos del usuario</h2>
          <div className="space-y-2 text-sm">
            {fields.map((f) => (
              <div key={f.label} className="flex items-center justify-between">
                <span className="text-gray-500">{f.label}</span>
                <span className="font-medium text-gray-900">{f.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded shadow p-4 flex flex-col gap-3">
          <h2 className="font-semibold">Wallet</h2>
          <p className="text-sm text-gray-600">
            Descarga tu wallet para futuras integraciones.
          </p>
          <button
            type="button"
            onClick={handleDownloadWallet}
            disabled={walletLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded border border-blue-600 text-blue-700 hover:bg-blue-50 w-fit disabled:opacity-60"
          >
            <Download size={18} />
            {walletLoading ? "Descargando..." : "Descargar wallet"}
          </button>
          {walletError && (
            <div className="text-sm text-red-600">{walletError}</div>
          )}
          {walletInfo && (
            <div className="text-sm text-gray-700">
              Estado: <span className="font-medium">{walletInfo.status ?? "—"}</span>
              {walletInfo.uuid && (
                <>
                  {" "}· UUID:{" "}
                  <span className="font-medium">{walletInfo.uuid}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
